import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { trackNewsletterSignup } from '../utils/analytics';

const NewsletterSignup = ({ variant = 'default' }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/forms/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to subscribe');
      }

      const data = await response.json();
      trackNewsletterSignup();
      setIsSuccess(true);
      setEmail('');
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
      
    } catch (err) {
      console.error('Newsletter subscription error:', err);
      setError('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (variant === 'footer') {
    return (
      <div className="w-full">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={20} style={{ color: '#FF7A1A' }} />
          <h3 className="text-lg font-bold" style={{ fontFamily: 'Fraunces, serif', color: '#FFFFFF' }}>
            Weekly Local Deals
          </h3>
        </div>
        <p className="text-sm mb-4" style={{ fontFamily: 'Inter, sans-serif', color: '#D1D5DB' }}>
          Get curated deals from local businesses delivered to your inbox.
        </p>
        
        {isSuccess ? (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-3 rounded-lg text-sm font-semibold"
            style={{ backgroundColor: '#10B98120', color: '#10B981' }}
          >
            ✓ You're subscribed!
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/60"
            />
            <Button
              type="submit"
              disabled={isSubmitting}
              className="px-4"
              style={{
                backgroundColor: '#FF7A1A',
                color: '#FFFFFF'
              }}
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Send size={16} />
              )}
            </Button>
          </form>
        )}
        {error && (
          <p className="text-xs mt-2" style={{ color: '#EF4444' }}>
            {error}
          </p>
        )}
      </div>
    );
  }

  // Default inline variant
  return (
    <div className="w-full max-w-md">
      {isSuccess ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-4 rounded-xl text-center font-semibold"
          style={{ backgroundColor: '#10B98120', color: '#10B981' }}
        >
          🎉 You're subscribed! Check your inbox.
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSubmitting}
            className="flex-1 rounded-xl"
          />
          <Button
            type="submit"
            disabled={isSubmitting}
            className="px-6 rounded-xl"
            style={{
              backgroundColor: '#FF7A1A',
              color: '#FFFFFF'
            }}
          >
            {isSubmitting ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </form>
      )}
      {error && (
        <p className="text-sm mt-2 text-center" style={{ color: '#EF4444' }}>
          {error}
        </p>
      )}
    </div>
  );
};

export default NewsletterSignup;
